import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Cookie, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

const STORAGE_KEY = "cookie_consent";

type Preferences = {
  essential: true;
  analytics: boolean;
  marketing: boolean;
};

type StoredConsent = {
  status: "accepted" | "custom";
  preferences: Preferences;
  timestamp: string;
};

export function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [managing, setManaging] = useState(false);
  const [prefs, setPrefs] = useState<Preferences>({
    essential: true,
    analytics: true,
    marketing: false,
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const existing = window.localStorage.getItem(STORAGE_KEY);
      if (!existing) setVisible(true);
    } catch {
      setVisible(true);
    }
  }, []);

  const persist = (data: StoredConsent) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      // ignore
    }
  };

  const acceptAll = () => {
    persist({
      status: "accepted",
      preferences: { essential: true, analytics: true, marketing: true },
      timestamp: new Date().toISOString(),
    });
    setVisible(false);
    setManaging(false);
  };

  const savePreferences = () => {
    persist({
      status: "custom",
      preferences: prefs,
      timestamp: new Date().toISOString(),
    });
    setVisible(false);
    setManaging(false);
  };

  if (!visible) return null;

  return (
    <>
      <div className="fixed inset-x-0 bottom-0 z-50 px-4 pb-4 sm:px-6 sm:pb-6">
        <div className="mx-auto max-w-5xl rounded-2xl border border-border bg-card/95 p-5 shadow-2xl backdrop-blur supports-[backdrop-filter]:bg-card/80">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="flex items-start gap-3 sm:flex-1">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
                <Cookie className="h-5 w-5 text-primary" />
              </div>
              <p className="text-sm leading-relaxed text-foreground">
                We use cookies to improve your experience and analyze site
                traffic. By clicking "Accept", you consent to our use of cookies.{" "}
                <Link
                  to="/cookies"
                  className="underline underline-offset-2 hover:text-primary"
                >
                  Learn more
                </Link>
                .
              </p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setManaging(true)}
              >
                Manage Preferences
              </Button>
              <Button size="sm" onClick={acceptAll}>
                Accept All
              </Button>
              <button
                aria-label="Dismiss"
                onClick={acceptAll}
                className="absolute right-3 top-3 text-muted-foreground hover:text-foreground sm:hidden"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={managing} onOpenChange={setManaging}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Cookie Preferences</DialogTitle>
            <DialogDescription>
              Choose which categories of cookies you allow. You can change this
              later from our Cookie Policy page.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-3">
              <div>
                <Label className="text-sm font-medium">Essential Cookies</Label>
                <p className="mt-1 text-xs text-muted-foreground">
                  Required for the site to function. Always on.
                </p>
              </div>
              <Switch checked disabled />
            </div>
            <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-3">
              <div>
                <Label className="text-sm font-medium">Analytics Cookies</Label>
                <p className="mt-1 text-xs text-muted-foreground">
                  Help us understand how visitors use AdKavi.
                </p>
              </div>
              <Switch
                checked={prefs.analytics}
                onCheckedChange={(v) =>
                  setPrefs((p) => ({ ...p, analytics: v }))
                }
              />
            </div>
            <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-3">
              <div>
                <Label className="text-sm font-medium">Marketing Cookies</Label>
                <p className="mt-1 text-xs text-muted-foreground">
                  Used to personalize ads and measure campaigns.
                </p>
              </div>
              <Switch
                checked={prefs.marketing}
                onCheckedChange={(v) =>
                  setPrefs((p) => ({ ...p, marketing: v }))
                }
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={acceptAll}>
              Accept All
            </Button>
            <Button onClick={savePreferences}>Save Preferences</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
